import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import camelcase from "lodash.camelcase";

import { useSort } from "./useSort";
import { useRawSort } from "hooks/useRawSort";

import { TFinalSort, TSort, TSortKey } from "components/Table/types";
import { DEFAULT_PAGINATION } from "components/Table/constants";

type TSortObject = {
  sort: TSort;
};

type TPagination = {
  page: number;
  pageSize: number;
};

type TQueryStringResult<T> = {
  filter?: T;
  sort?: Record<string, TFinalSort>;
  page?: TPagination;
};

type UseTableSearchParamsParams<T> = {
  defaultData?: TQueryStringResult<T>;
  sortMap?: Record<string, string>;
  multiSort?: boolean;
};

const OBJECT_KEY = {
  SORT: "sort",
  PAGE_SIZE: "pageSize",
};

const QUERY_STRING_KEY = {
  SORT: "sort",
  PAGE_SIZE: "page-size",
  PAGE: "page",
};

const PARAMS_REQUIRED_RESET_PAGE = [OBJECT_KEY.PAGE_SIZE, OBJECT_KEY.SORT];

export const useTableQueryParams = <T>({
  defaultData,
  sortMap,
  multiSort,
}: UseTableSearchParamsParams<T>) => {
  const constructedDefaultValue = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [objSearchParams, setObjSearchParams] =
    useState<TQueryStringResult<T>>();
  const [requiredRefresh, setRequiredRefresh] = useState(true);
  const [sort, setSort, replaceSort] = useRawSort(defaultData?.sort || {});
  const constructedSort = useSort(sort, multiSort);

  /**
   * Set default page data when it's not provided from defaultData
   */
  const defaultValue: TQueryStringResult<T> = useMemo(() => {
    return {
      page: {
        page: DEFAULT_PAGINATION.currentPage,
        pageSize: DEFAULT_PAGINATION.itemPerPage,
      },
      ...defaultData,
    };
  }, [defaultData]);

  /**
   * Used to build the filter data based on the given filter key
   */
  const filterKeys = useMemo(() => {
    const keys = [];
    for (const key in defaultValue?.filter) {
      keys.push(key);
    }

    return keys;
  }, [defaultValue?.filter]);

  const handleSetSearchParams = useCallback(
    (params: Partial<T | TPagination | TSortObject>) => {
      let newParams: Record<string, string> = {};
      const deletedParams: string[] = [];

      for (const key in params) {
        if (deletedParams.includes(key)) continue;

        if (!(params as any)?.[key]) {
          deletedParams.push(key);
        } else {
          if (key === OBJECT_KEY.SORT) {
            const newSort = (params as TSortObject).sort;
            setSort(newSort, multiSort);
            const stringSort = convertSortObjToString(
              {
                ...sort,
                [newSort.key]: {
                  ...newSort,
                  order: new Date().getTime(),
                },
              },
              multiSort
            );

            if (stringSort) {
              newParams = {
                ...newParams,
                sort: stringSort,
              };
            } else {
              deletedParams.push(key);
            }
          } else {
            newParams = {
              ...newParams,
              [camelToHypen(key)]: `${(params as any)[key]}`,
            };
          }
        }
      }

      const paramKeys = Object.keys(params);
      const requiredResetPage = paramKeys.some((key) =>
        [...PARAMS_REQUIRED_RESET_PAGE, ...filterKeys].includes(key)
      );

      if (requiredResetPage) {
        newParams = {
          ...newParams,
          [QUERY_STRING_KEY.PAGE]: "1",
        };
      }

      setSearchParams(
        (prev) => {
          const existingParams: Record<string, string> = {};

          prev.forEach((param, key) => {
            if (!deletedParams.includes(camelcase(key as string))) {
              const hypenedKey = camelToHypen(key as string);
              existingParams[hypenedKey] = param;
            }
          });

          return {
            ...existingParams,
            ...newParams,
          };
        },
        {
          replace: true,
          preventScrollReset: true,
        }
      );
      setTimeout(() => {
        setRequiredRefresh(true);
      }, 100);
    },
    [setSearchParams, setSort, sort, filterKeys, multiSort]
  );

  useLayoutEffect(() => {
    if (
      defaultValue &&
      (searchParams as any)?.size === 0 &&
      !constructedDefaultValue.current
    ) {
      let newParams: Record<string, string> = {};
      for (const key in defaultValue) {
        if (key === OBJECT_KEY.SORT) {
          if (!defaultValue?.sort) continue;
          const stringSort = convertSortObjToString(
            defaultValue.sort,
            multiSort
          );

          if (stringSort) {
            newParams = {
              ...newParams,
              sort: convertSortObjToString(defaultValue.sort, multiSort),
            };
          }
        } else {
          for (const param in (defaultValue as any)?.[key]) {
            if ((defaultValue as any)?.[key]?.[param]) {
              newParams = {
                ...newParams,
                [camelToHypen(param)]: (defaultValue as any)?.[key]?.[param],
              };
            }
          }
        }
      }

      setSearchParams(newParams, {
        replace: true,
        preventScrollReset: true,
      });
      setRequiredRefresh(true);
      constructedDefaultValue.current = true;
    }
  }, [defaultValue, searchParams, multiSort, setSearchParams]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (requiredRefresh) {
        let tmpFilterParams = {} as T;

        for (const key of filterKeys) {
          tmpFilterParams = {
            ...tmpFilterParams,
            [camelcase(key as string)]:
              searchParams.get(camelToHypen(key as string)) || undefined,
          };
        }

        const currentPage = searchParams.get(QUERY_STRING_KEY.PAGE);
        const itemPerPage = searchParams.get(QUERY_STRING_KEY.PAGE_SIZE);
        const sortParam = searchParams.get(QUERY_STRING_KEY.SORT);

        const splitedSort = sortParam?.split(", ") || [];
        let existingSort = sort || {};

        for (let i = 0; i < splitedSort?.length; i++) {
          const [sortKey, sortType] = splitedSort[i]?.split(" ") || [];
          /**
           * Skip if key or type of the sort not exists
           * Also skip if the type of sort is not asc or desc
           */
          if (
            !sortKey ||
            !sortType ||
            !["asc", "desc", "none"].includes(sortType)
          ) {
            continue;
          }
          const field =
            sortMap?.[sortKey] || (existingSort?.[sortKey] ? sortKey : "");

          if (!existingSort?.[field]) continue;

          existingSort = {
            ...existingSort,
            [field]: {
              ...existingSort[field],
              type: sortType as TSortKey,
              /**
               * Order used to construct the order of the filter
               * The bigger order (descending order) will be placed frst
               * This is required to let the BE know which key of filter that we are want to prioritize
               */
              order:
                existingSort[field].order ||
                (Object.keys(existingSort).length || 0) - i,
            },
          };
        }

        replaceSort(existingSort);
        setObjSearchParams({
          page: {
            page: currentPage ? Number(currentPage) : 1,
            pageSize: itemPerPage ? Number(itemPerPage) : 10,
          },
          filter: tmpFilterParams,
        });
        setRequiredRefresh(false);
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [sortMap, sort, searchParams, requiredRefresh, filterKeys, replaceSort]);

  return {
    searchParams: {
      ...objSearchParams,
      sort,
      constructedSort,
    },
    onSetSearchParams: handleSetSearchParams,
  };
};

const camelToHypen = (camelCaseString: string) => {
  return camelCaseString.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
};

export const convertSortObjToString = (
  sort: Record<string, TFinalSort>,
  multiSort?: boolean
) => {
  let tmpSort = "";
  const arrSort: TFinalSort[] = [];
  for (const key in sort) {
    if (sort[key].type !== "none") {
      arrSort.push(sort[key]);
    }
  }

  arrSort.sort((a, b) => ((a.order || 0) < (b.order || 0) ? 1 : -1));

  for (const _sort of arrSort) {
    const newSort = `${_sort.fieldName} ${_sort.type}`;
    if (tmpSort) {
      tmpSort = `${tmpSort}, ${newSort}`;
    } else {
      tmpSort = newSort;
      if (!multiSort) break;
    }
  }
  return tmpSort;
};
